import React, { useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import lpt1 from '../img/lightning protection system/lpt1.jpg';
import lpt3 from '../img/lightning protection system/lpt3.jpg';

function LightningProtection() {
  useEffect(() => {
    AOS.init({ duration: 800 });
  }, []);

  return (
    <section className="py-5 mt-5 bg-light" id="lightning-protection">
      <div className="container">
        <div className="text-center mb-5" data-aos="fade-up">
          <h2 className="fw-bold text-dark">⚡ Lightning Protection System (LPS)</h2>
          <p className="text-muted">
            Protection of buildings, substations, hospitals, data centers, telecom towers, educational institutions & government buildings and critical infrastructure from lightning damage.
          </p>
        </div>

        <div className="row mb-5" data-aos="zoom-in">
          <div className="col-md-6 mb-3">
            <img
              src={lpt3}
              alt="Busbar Trunking System"
              className="img-fluid rounded shadow"
              style={{ maxHeight: '325px', objectFit: 'cover', width: '100%' }}
            />
          </div>
          <div className="col-md-6 mb-3">
            <img
              src={lpt1}
              alt="Busbar System Setup"
              className="img-fluid rounded shadow"
              style={{ maxHeight: '325px', objectFit: 'cover', width: '100%' }}
            />
          </div>
        </div>

        {/* IEC Standards */}
        <div className="mb-4" data-aos="fade-up">
          <h4 className="text-info mb-3">📘 IEC Standards</h4>
          <ul className="text-muted">
            <li><strong>IEC 62305 (Parts 1–4):</strong> Protection against lightning</li>
            <li><strong>NF C 17-102:</strong> For ESE-type Air Terminals</li>
          </ul>
        </div>

        {/* Parameters */}
        <div className="mb-4" data-aos="fade-up">
          <h4 className="text-primary mb-3">📏 Parameters</h4>
          <ul className="text-muted">
            <li><strong>Air Terminal Type:</strong> ESE (Early Streamer Emission) or Conventional Rod</li>
            <li><strong>Coverage Radius:</strong> Up to 107 meters (depending on ESE model & height)</li>
            <li><strong>Response Time (ESE):</strong> 10–60 µs (microseconds)</li>
            <li><strong>Material:</strong> High-conductivity copper, stainless steel, or aluminum</li>
            <li><strong>Down Conductors:</strong> Single/multiple 50mm² copper strip or cable</li>
            <li><strong>Earth Resistance:</strong> &lt;1 ohm recommended</li>
            <li><strong>Surge Protection Devices:</strong> Type 1, 2, and 3 (as per IEC 61643)</li>
          </ul>
        </div>

        {/* Key Features */}
        <div className="mb-5" data-aos="fade-up">
          <h4 className="text-success mb-3">✨ Key Features</h4>
          <ul className="text-muted">
            <li>Complete protection from direct strikes and induced surges</li>
            <li>Early streamer emission (ESE) for wide area coverage</li>
            <li>Corrosion-resistant materials for long life</li>
            <li>Integrates with earthing and surge protection systems</li>
            <li>Easy to install & low maintenance</li>
            <li>Designed to comply with local and international standards</li>
          </ul>
        </div>

        {/* Overview */}
        <div className="mb-4" data-aos="fade-up">
          <p className="text-muted lead">
            A Lightning Protection System (LPS) is designed to safeguard structures and their occupants from the damaging effects of lightning strikes.
            These systems are critical for protecting buildings, electrical equipment, and sensitive electronics from the high voltage and currents associated with lightning.
          </p>
        </div>

        {/* Benefits */}
        <div className="mb-5" data-aos="fade-up">
          <h4 className="text-success mb-3">Benefits</h4>
          <ul className="text-muted">
            <li><strong>Safety:</strong> Reduces injury or fatality risks during lightning storms.</li>
            <li><strong>Equipment Protection:</strong> Prevents damage to sensitive electronics.</li>
            <li><strong>Insurance & Compliance:</strong> Meets regulatory and insurance requirements.</li>
            <li><strong>Peace of Mind:</strong> Confidence for occupants and building owners.</li>
          </ul>
        </div>

        {/* Applications */}
        <div className="mb-4" data-aos="fade-up">
          <h4 className="text-dark mb-3">Applications</h4>
          <div className="row g-4">
            {[
              'Commercial Buildings',
              'Industrial Facilities',
              'Residential Properties',
              'Telecommunication & Data Centers'
            ].map((item, idx) => (
              <div className="col-md-6" key={idx}>
                <div className="p-3 bg-white shadow-sm rounded h-100 border-start border-3 border-warning">
                  <h6 className="text-dark fw-semibold mb-2">{item}</h6>
                  <p className="text-muted small mb-0">
                    {item === 'Commercial Buildings' && 'Office buildings, malls, warehouses — protection from costly lightning damage.'}
                    {item === 'Industrial Facilities' && 'Essential for factories and manufacturing units with critical equipment.'}
                    {item === 'Residential Properties' && 'Useful for homes in storm-prone areas to protect families and property.'}
                    {item === 'Telecommunication & Data Centers' && 'Shields critical infrastructure from surge-related failures.'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default LightningProtection;
